import React from 'react';
import { X, Calendar, Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Notification } from '../../types';
import { Button } from './Button';

interface NotificationModalProps {
    notification: Notification | null;
    onClose: () => void;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({ notification, onClose }) => {
    if (!notification) return null;

    const getIcon = (type: string) => {
        switch (type) {
            case 'success': return <CheckCircle className="h-6 w-6 text-green-500" />;
            case 'warning': return <AlertTriangle className="h-6 w-6 text-yellow-500" />;
            case 'error': return <XCircle className="h-6 w-6 text-red-500" />;
            default: return <Info className="h-6 w-6 text-blue-500" />;
        }
    };

    const getBgColor = (type: string) => {
        switch (type) {
            case 'success': return 'bg-green-50';
            case 'warning': return 'bg-yellow-50';
            case 'error': return 'bg-red-50';
            default: return 'bg-blue-50';
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
                {/* Header */}
                <div className={`px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center ${getBgColor(notification.type)} bg-opacity-80 dark:bg-opacity-20`}>
                    <div className="flex items-center gap-3">
                        {getIcon(notification.type)}
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Notification Details</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-black/5 rounded-full transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{notification.title}</h2>

                    <div className="flex items-center text-sm text-gray-500 mb-4">
                        <Calendar className="h-4 w-4 mr-2" />
                        {new Date(notification.createdAt).toLocaleString()}
                    </div>

                    <div className="prose prose-sm max-w-none text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                        {notification.message}
                    </div>

                    {notification.link && (
                        <div className="mt-6">
                            <Button
                                className="w-full"
                                onClick={() => {
                                    window.location.href = notification.link!;
                                    onClose();
                                }}
                            >
                                View Related Content
                            </Button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </div>
    );
};
